import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { CardItem } from '@/components/CardItem'
import { AppIcons } from '@/components/AppIcons'
import { ActivityItem } from '@/components/ActivityItem'
import {
  useGetTransactions,
  useGetWallet,
  transactionServices,
  Transaction,
} from '@/domain'
import { useTheme } from '@/hooks/useTheme'
import { responsiveAdapter } from '@/utils'
import { formatTransactionItem } from '@/utils/formatTransactionItem'

// Card transaction types matching mobile
const CARD_TRANSACTION_TYPES = ['card_write_off', 'card_top_up', 'cashback_payout'] as const

// Get display name for card transactions
const getCardTransactionName = (transaction: Transaction): string => {
  switch (transaction.type) {
    case 'card_write_off':
      return transaction.subject_card?.details || 'Card Payment'
    case 'card_top_up':
      return 'Card Top Up'
    case 'cashback_payout':
      return 'Cashback'
    default:
      return 'Card Transaction'
  }
}

export const LatestCardActivities: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()

  // Fetch all transactions and filter for card transactions
  const { data: transactionsData, isLoading } = useGetTransactions({
    types: CARD_TRANSACTION_TYPES as any,
    page_size: 10, // Get more to ensure we have 3 card transactions
  })

  // Get wallet data for currency info
  const { data: walletData } = useGetWallet()

  // Create currency map for enhanced transaction display
  const currencyMap = useMemo(() => {
    if (!walletData?.completeCurrencies) return {}

    return walletData.completeCurrencies.reduce(
      (acc, currency) => {
        const currencyCode = currency.code?.toLowerCase()?.trim()
        if (currencyCode) {
          acc[currencyCode] = {
            icon: currency.icon,
            avatarBGRefColor: currency.avatarBGRefColor,
            name: currency.name,
            symbol: currency.symbol,
          }
        }
        return acc
      },
      {} as Record<
        string,
        {
          icon?: string
          avatarBGRefColor?: string | [string, string, ...string[]]
          name?: string
          symbol?: string
        }
      >,
    )
  }, [walletData?.completeCurrencies])

  // Filter and enhance card transactions
  const cardTransactionsList = useMemo(() => {
    if (!transactionsData?.pages) return []

    const allTransactions = transactionsData.pages.flatMap(
      (page) => page.transactions,
    )

    // Filter for card transactions only
    const cardTransactions = allTransactions.filter((transaction) =>
      CARD_TRANSACTION_TYPES.includes(transaction.type as any),
    )

    // Since we've already filtered for card transactions only, all transactions here are card transactions
    return cardTransactions.map((transaction) => {
      // For card transactions, use card-specific data without any currency details
      const enhancedTransaction = {
        ...transaction,
        // Card transaction - no currency details, use card-specific styling
        currencyIcon: undefined,
        currencyColor: ['#8B5CF6', '#8B5CF6'] as [string, string],
        currencyName: undefined,
        currencySymbol: undefined,
        currencyIconUrl: undefined,
        currencyColorReference: ['#8B5CF6', '#8B5CF6'] as [string, string],
      }

      const finalEnhancedTransaction = {
        ...enhancedTransaction,
        // Add formatted fields that ActivityItem expects
        formattedAmount:
          transactionServices.formatTransactionAmount(transaction).amount,
        formattedDate: new Date(transaction.created_at).toLocaleDateString(),
        formattedTime: new Date(transaction.created_at).toLocaleTimeString(),
        subTitle: '',
      }

      // Format transaction using RN-style formatter
      const formattedParams = formatTransactionItem({
        item: finalEnhancedTransaction,
        t,
      })

      // Since we're only dealing with card transactions, override with card-specific display
      const cardFormattedParams = {
        ...formattedParams,
        // Card-specific display
        iconName: 'card',
        iconSize: 24,
        backgroundColor: ['#6B7280', '#9CA3AF'] as [string, string],
        name: getCardTransactionName(transaction),
        title: getCardTransactionName(transaction),
      }

      return {
        ...finalEnhancedTransaction,
        ...cardFormattedParams,
      }
    })
  }, [transactionsData?.pages, currencyMap, theme, t])

  const handleOpenCardActivities = () => {
    navigate('/kem-card/activities')
  }

  // Loading state
  if (isLoading) {
    return (
      <BaseView
        bgColor="background.cardItemRoot"
        rounded={16}
        style={{
          width: '100%',
          height: '100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <BaseText color="label.description">
          {t('translation:loading') || 'Loading...'}
        </BaseText>
      </BaseView>
    )
  }

  // Don't show if no card transactions
  if (cardTransactionsList.length === 0) {
    return (
      <BaseView gap={12}>
        <BaseText fontSize={18} fontFamily="semibold" color="label.default">
          {t('translation:activity', 'Activity')}
        </BaseText>

        <BaseView
          bgColor="background.cardItemRoot"
          rounded={24}
          p={16}
          row
          gap={12}
          align="center"
        >
          <BaseView
            style={{
              width: 44,
              height: 44,
              backgroundColor: theme.colors.background.secondaryGray,
              borderRadius: 22,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AppIcons name="card" color="#9CA3AF" size={24} />
          </BaseView>

          <BaseView flex={1}>
            <BaseText fontSize={16} fontFamily="medium" color="label.default">
              {t('translation:transactionsNotFound') ||
                'No card transactions yet'}
            </BaseText>
            <BaseText
              fontSize={14}
              fontFamily="regular"
              color="label.description"
            >
              {t('translation:emptyTransactions') ||
                'Start using your card to see transactions here'}
            </BaseText>
          </BaseView>
        </BaseView>
      </BaseView>
    )
  }

  return (
    <BaseView gap={12}>
      <BaseView row align="center" justify="space-between">
        <BaseText fontSize={18} fontFamily="semibold" color="label.default">
          {t('translation:kemCard.activity')}
        </BaseText>

        {cardTransactionsList.length > 0 && (
          <BaseText
            fontSize={14}
            fontFamily="medium"
            color="label.clickable"
            style={{ cursor: 'pointer' }}
            onClick={handleOpenCardActivities}
          >
            {t('translation:seeAll') || 'See All'}
          </BaseText>
        )}
      </BaseView>

      <CardItem.Root
        style={{
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: 0,
        }}
        onPress={
          cardTransactionsList.length > 3 ? handleOpenCardActivities : undefined
        }
      >
        <BaseView fullWidth>
          {/* Transactions list - limit to 3 */}
          <BaseView>
            {cardTransactionsList.slice(0, 3).map((transaction) => (
              <div
                key={transaction.transaction_id}
                onClick={(e) => {
                  e.stopPropagation()
                }}
              >
                <ActivityItem
                  height={responsiveAdapter(72)}
                  isLastActivities={true}
                  {...transaction}
                  onPress={() =>
                    navigate(
                      `/transaction-details/${transaction.transaction_id}`,
                    )
                  }
                />
              </div>
            ))}
          </BaseView>
        </BaseView>
      </CardItem.Root>
    </BaseView>
  )
}
